import { ref, computed, onUnmounted } from 'vue';

export default function useCountdownTimer(targetDateString, timeLimit = 600) {
  const remainingSeconds = ref(0);
  const isExpired = ref(false);
  const intervalId = ref(null);
  
  // Calculate the countdown display in MM:SS format
  const countdownDisplay = computed(() => {
    if (remainingSeconds.value <= 0) {
      return '00:00';
    }
    
    const minutes = Math.floor(remainingSeconds.value / 60);
    const seconds = remainingSeconds.value % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });
  
  // Calculate how much percentage of time is left (for progress indicators)
  const percentRemaining = computed(() => {
    return Math.max(0, Math.min(100, (remainingSeconds.value / timeLimit) * 100));
  });
  
  // Get color based on remaining time
  const countdownColor = computed(() => {
    if (remainingSeconds.value <= 0) {
      return 'error';
    } else if (remainingSeconds.value < 60) {
      return 'error'; // Red when less than 1 minute
    } else if (remainingSeconds.value < 180) {
      return 'warning'; // Yellow when less than 3 minutes
    } else {
      return 'success'; // Green otherwise
    }
  });
  
  // Start the countdown
  const startCountdown = () => {
    // First, calculate the target end time (dtpedido + 10 minutes)
    if (!targetDateString) {
      isExpired.value = true;
      return;
    }
    
    const targetDate = new Date(targetDateString);
    const endTime = new Date(targetDate.getTime() + (timeLimit * 1000)); // timeLimit in seconds
    
    // Calculate initial remaining time
    updateRemainingTime(endTime);
    
    // Start interval to update countdown
    intervalId.value = setInterval(() => {
      updateRemainingTime(endTime);
    }, 1000);
  };
  
  // Update the remaining time
  const updateRemainingTime = (endTime) => {
    const now = new Date();
    const timeDiff = endTime - now;
    
    if (timeDiff <= 0) {
      remainingSeconds.value = 0;
      isExpired.value = true;
      
      // Clear the interval if expired
      if (intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
      }
    } else {
      remainingSeconds.value = Math.floor(timeDiff / 1000);
      isExpired.value = false;
    }
  };
  
  // Clean up on component unmount
  onUnmounted(() => {
    if (intervalId.value) {
      clearInterval(intervalId.value);
      intervalId.value = null;
    }
  });
  
  // Start the countdown immediately
  startCountdown();
  
  return {
    remainingSeconds,
    isExpired,
    countdownDisplay,
    percentRemaining,
    countdownColor
  };
}
